/**
 * How a stored field value is rendered in a version trail.
 *
 * A version diff shows values it was handed, as TEXT — so a rich-text column
 * (a description, acceptance criteria, a work item's completion notes) arrives
 * as its stored HTML and reads back as the literal tags:
 * `Changed Completion notes from <p dir="auto">…</p><ul><li>…`. That has always
 * been true of every entity with a rich-text field; it became the routine case
 * for a Software Factory work item when the ship transition started writing
 * `completion_notes` on every ship (WI 5053fc4f).
 *
 * Two decisions, and both are deliberate:
 *
 * - **Flattened to text, never rendered as markup.** A version trail is exactly
 *   the surface that must not re-render whatever markup it was handed — it is a
 *   record OF a value, not a presentation of it, and the values it holds are
 *   historical rows nothing re-sanitizes on the way past.
 * - **Truncated.** A ship write-up runs to several paragraphs, and one of them
 *   laid end-to-end across a diff row is the same unreadable defect wearing
 *   different clothes. The full value is one click away in the entity itself.
 *
 * Lives beside the two components rather than inside either, because BOTH the
 * timeline summary and the detail pane render these values and a copy in one of
 * them is how the other silently keeps showing tags.
 */
import { htmlToPlainText } from '../../utils/htmlToText';

/** Enough for a one-line summary to be recognisable; short enough to stay one line. */
export const SUMMARY_VALUE_MAX_CHARS = 120;

/** True when a stored value carries markup rather than being plain text. */
function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

/**
 * A stored value as it belongs in a version-trail row.
 *
 * Returns the empty string for a null/undefined value so callers can keep using
 * their existing falsy checks to pick the "set"/"cleared"/"changed" wording.
 */
export function summaryValue(value: string | null | undefined): string {
  if (!value) return '';
  const flat = looksLikeHtml(value) ? htmlToPlainText(value).replace(/\s+/g, ' ').trim() : value;
  return flat.length > SUMMARY_VALUE_MAX_CHARS
    ? `${flat.slice(0, SUMMARY_VALUE_MAX_CHARS).trimEnd()}…`
    : flat;
}
